import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDropdown } from "../../hooks/useDropdown";
import classes from "./ServiceCard.module.css"
import { useTextTruncate } from '../../hooks/useTextTruncate';
import { getS3AudioUrl } from '../../util/s3Utils';
import { getCloudFrontOriginalImageUrl, getDirectS3StudioImageUrl } from '../../util/cloudFrontUtils';

function canUseStorageFallbackKey(key) {
  if (!key || typeof key !== 'string') return false;
  const t = key.trim();
  if (!t) return false;
  if (/^https?:\/\//i.test(t)) {
    return /amazonaws\.com/i.test(t) || /cloudfront\.net/i.test(t);
  }
  return true;
}

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 28 34" fill="none">
    <path d="M26.6267 14.3973C28.3172 15.4366 28.3172 18.035 26.6267 19.0744L3.80507 33.1059C2.11458 34.1452 0.00147438 32.846 0.00147438 30.7673L0.00147438 2.70438C0.00147438 0.625646 2.11458 -0.67356 3.80507 0.365807L26.6267 14.3973Z" fill="white"/>
  </svg>
);

const ImageWithSkeleton = ({ src, alt, round, loading, fallbackStorageKey }) => {
  const [loaded, setLoaded] = useState(() => !src);
  const [recoverPhase, setRecoverPhase] = useState(0);
  const [cacheBustToken, setCacheBustToken] = useState(0);
  const imgRef = useRef(null);
  const prevSrcRef = useRef(src);

  const resolvedSrc = useMemo(() => {
    if (!src) return '';
    if (recoverPhase === 0) return src;
    if (recoverPhase === 1) {
      const sep = src.includes('?') ? '&' : '?';
      return `${src}${sep}_retry=${cacheBustToken || 1}`;
    }
    if (!fallbackStorageKey || !canUseStorageFallbackKey(fallbackStorageKey)) {
      return src;
    }
    if (recoverPhase === 2) {
      return getCloudFrontOriginalImageUrl(fallbackStorageKey) || '';
    }
    if (recoverPhase === 3) {
      return getDirectS3StudioImageUrl(fallbackStorageKey) || '';
    }
    return src;
  }, [src, recoverPhase, cacheBustToken, fallbackStorageKey]);

  useEffect(() => {
    if (prevSrcRef.current === src) return;
    prevSrcRef.current = src;
    setLoaded(!src);
    setRecoverPhase(0);
    setCacheBustToken(0);
  }, [src]);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [resolvedSrc, recoverPhase]);

  const handleImgError = () => {
    if (recoverPhase === 0 && src) {
      setCacheBustToken(Date.now());
      setRecoverPhase(1);
      setLoaded(false);
      return;
    }
    if (recoverPhase === 1 && fallbackStorageKey && canUseStorageFallbackKey(fallbackStorageKey)) {
      const orig = getCloudFrontOriginalImageUrl(fallbackStorageKey);
      if (orig && orig !== src) {
        setRecoverPhase(2);
        setLoaded(false);
        return;
      }
    }
    if ((recoverPhase === 1 || recoverPhase === 2) && fallbackStorageKey && canUseStorageFallbackKey(fallbackStorageKey)) {
      const direct = getDirectS3StudioImageUrl(fallbackStorageKey);
      if (direct && direct !== resolvedSrc) {
        setRecoverPhase(3);
        setLoaded(false);
        return;
      }
    }
    setLoaded(true);
  };

  return (
    <div className={`${classes.mediaImageWrap} ${round ? classes.mediaImageWrapRound : ""}`}>
      {!loaded && (
        <div
          className={`${classes.mediaSkeleton} ${round ? classes.mediaSkeletonRound : ""}`}
          aria-hidden
        />
      )}
      {resolvedSrc ? (
        <img
          ref={imgRef}
          key={`${resolvedSrc}|${recoverPhase}`}
          src={resolvedSrc}
          alt={alt}
          loading={loading}
          onLoad={() => setLoaded(true)}
          onError={handleImgError}
          className={`${classes.mediaImg} ${loaded ? classes.mediaImgVisible : classes.mediaImgHidden}`}
        />
      ) : null}
    </div>
  );
};

export const ServiceCard = ({ creative }) => {
  const { textRef: nameTextRef, containerRef: nameContainerRef, truncatedText: truncatedName } = useTextTruncate(creative?.displayName || '');
  const { textRef: jobTitleTextRef, containerRef: jobTitleContainerRef, truncatedText: truncatedJobTitle } = useTextTruncate(creative?.jobTitle || '');

    const navigate = useNavigate();
    const { isOpen: showDropdown, toggle: toggleDropdown, triggerRef, dropdownRef } = useDropdown();
    const [isFinished, setIsFinished] = useState(false);
    const [visibleSkillsCount, setVisibleSkillsCount] = useState(2);
    const tagsContainerRef = useRef(null);
    const [resolvedAudioUrls, setResolvedAudioUrls] = useState({});
    const [currentlyPlayingKey, setCurrentlyPlayingKey] = useState(null);
    const audioRef = useRef(null);
    
    const handleProcessLinkClick = (e, path) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(path);
    };

    const isMusik = creative?.creativeType === 'MUSIC';
    const musicAudios = (creative?.allProcessedProjectAudios || []).slice(0, 5);

    const handleAudioPlayClick = useCallback(async (e, audio) => {
        e.preventDefault();
        e.stopPropagation();
        const key = audio?.audioKey || audio?.originalUrl;
        if (!key) return;
        const audioEl = audioRef.current;
        if (!audioEl) return;
        if (currentlyPlayingKey === key) {
            audioEl.pause();
            setCurrentlyPlayingKey(null);
            return;
        }
        let url = resolvedAudioUrls[key];
        if (!url) {
            url = await getS3AudioUrl(key);
            setResolvedAudioUrls(prev => ({ ...prev, [key]: url }));
        }
        audioEl.src = url;
        audioEl.play();
        setCurrentlyPlayingKey(key);
    }, [currentlyPlayingKey, resolvedAudioUrls]);

    useEffect(() => {
        if (!isMusik || !musicAudios.length) return;
        const audioEl = audioRef.current;
        const onEnded = () => setCurrentlyPlayingKey(null);
        if (audioEl) audioEl.addEventListener('ended', onEnded);
        return () => { if (audioEl) audioEl.removeEventListener('ended', onEnded); };
    }, [isMusik, musicAudios.length]);

    // Process media items
    const processedMediaItems = useMemo(() => {
        if (!creative?.allProcessedProjectImages?.length) return [];
      
        return creative.allProcessedProjectImages.slice(0, 5)
          .map((item) => {
            const isLink =
              item.type === 'link' ||
              (typeof item.url === 'string' &&
                (item.url.includes('youtube.com') ||
                 item.url.includes('youtu.be') ||
                 item.url.includes('vimeo.com')));
      
            const isVideoFile =
              item.type === 'video' ||
              (typeof item.url === 'string' &&
                /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(item.url));
      
            let displayUrl = '';
      
            if (isLink) {
              // OLD BEHAVIOR for YouTube/Vimeo → use raw thumbnailUrl
              displayUrl = item.thumbnailUrl || '';
            } else if (isVideoFile && (item.processedThumbnailUrl || item.thumbnailUrl)) {
              // Uploaded video with thumbnail → use processed thumbnail if available, otherwise raw
              displayUrl = item.processedThumbnailUrl || item.thumbnailUrl || '';
            } else {
              // Pure images
              displayUrl = item.processedUrl || item.url || '';
            }
      
            return {
              ...item,
              displayUrl,
              isLink,
              isVideoFile,
              isPhoto: !isLink && !isVideoFile,
            };
          })
          // Keep items that actually have something to show
          .filter((item) => !!item.displayUrl);
      }, [creative?.allProcessedProjectImages]);

    // Determine card type based on 4 cases:
    // 1. Finished, can be published, and active (PUBLISHED) - show finished card with "aktiv"
    // 2. Can be published but inactive (INACTIVE) - show finished card with "inaktiv"
    // 3. Package missing (project exists but package doesn't) - show process card
    // 4. Both package and project missing - show process card
    
    useEffect(() => {
        const profileExists = !!(creative?.displayName && creative?.description);
        // Note: projects and packages come as arrays from ServicesStudiosPage mapping
        const hasActiveProjects = Array.isArray(creative?.projects) 
            ? creative.projects.some(p => p.status === 'PUBLISHED')
            : (creative?.projects?.items?.some(p => p.status === 'PUBLISHED') ?? false);
        const hasActivePackages = Array.isArray(creative?.packages)
            ? creative.packages.some(p => p.isActive === true)
            : (creative?.packages?.items?.some(p => p.isActive === true) ?? false);
        
        // Check canBePublished from backend, with fallback
        const canBePublished = creative?.canBePublished ?? (hasActivePackages && hasActiveProjects);
        
        // Finished means: profile exists AND can be published (both package and project exist)
        // If profile exists but can't be published, show process card
        const finished = profileExists && canBePublished;
        
        setIsFinished(finished);
    }, [creative]);

    const calculateVisibleSkills = useCallback(() => {
        if (!tagsContainerRef.current || !creative?.skills?.length) {
            setVisibleSkillsCount(2);
            return;
        }

        const container = tagsContainerRef.current;
        const containerWidth = container.offsetWidth;
        const gap = 3; // gap between items from CSS
        
        // Get computed styles from container to match font rendering
        const computedStyle = window.getComputedStyle(container);
        const fontFamily = computedStyle.fontFamily || 'var(--primary-font)';
        
        // Create temporary elements to measure widths with accurate styling
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.whiteSpace = 'nowrap';
        tempContainer.style.top = '-9999px';
        tempContainer.style.left = '-9999px';
        tempContainer.style.fontFamily = fontFamily;
        tempContainer.style.fontSize = '1rem'; // regularMiddle font size
        tempContainer.style.fontWeight = '400';
        tempContainer.style.fontFeatureSettings = "'ss01' on, 'clig' off, 'liga' off";
        tempContainer.style.letterSpacing = '-0.3px';
        tempContainer.style.padding = '8px 16px';
        tempContainer.style.borderRadius = '100px';
        tempContainer.style.border = '1px solid #E2E8EF';
        tempContainer.style.background = '#FFFFFF';
        document.body.appendChild(tempContainer);

        // Measure each skill width
        const skillWidths = creative.skills.map(skill => {
            tempContainer.textContent = skill;
            return tempContainer.offsetWidth;
        });
        
        // Measure button width (will recalculate with actual count)
        tempContainer.style.padding = '8px 12px';
        tempContainer.style.fontSize = '1.031rem'; // mediumLarge font size
        tempContainer.style.fontWeight = '500';
        tempContainer.style.letterSpacing = '-0.2px';
        
        let totalWidth = 0;
        let count = 0;
        
        for (let i = 0; i < creative.skills.length; i++) {
            const skillWidth = skillWidths[i];
            const remainingSkills = creative.skills.length - (i + 1);
            
            // Calculate button width if there are remaining skills
            let buttonWidth = 0;
            if (remainingSkills > 0) {
                tempContainer.textContent = `+${remainingSkills}`;
                buttonWidth = tempContainer.offsetWidth;
            }
            
            // Check if we can fit this skill + button (if there are more skills)
            const requiredWidth = totalWidth + skillWidth + (remainingSkills > 0 ? gap + buttonWidth : 0);
            
            if (requiredWidth <= containerWidth) {
                totalWidth += skillWidth + (i > 0 ? gap : 0);
                count++;
            } else {
                break;
            }
        }
        
        document.body.removeChild(tempContainer);
        setVisibleSkillsCount(Math.max(1, count));
    }, [creative?.skills]);

    useEffect(() => {
        if (!isFinished || !creative?.skills?.length) return;
        
        calculateVisibleSkills();
        
        const resizeObserver = new ResizeObserver(() => {
            calculateVisibleSkills();
        });
        
        if (tagsContainerRef.current) {
            resizeObserver.observe(tagsContainerRef.current);
        }
        
        // Also observe window resize
        window.addEventListener('resize', calculateVisibleSkills);
        
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', calculateVisibleSkills);
        };
    }, [isFinished, creative?.skills, calculateVisibleSkills]);
    
  if (!creative) return null;

  return (
    <>
      {!isFinished ? (
        <Link to={`/creative-editing/${creative?.id}`} className={`${classes.serviceCard} ${classes.processCard}`}>
            <div className={classes.processCardContent}>
                <header className={classes.publishedHeader}>
                    <div className={classes.publishedImageContainer}>
                        <ImageWithSkeleton
                          src={creative?.profilePicture}
                          alt={creative?.displayName || ""}
                          round
                          fallbackStorageKey={creative?.profilePictureKey}
                        />
                    </div>
                    <div className={classes.publishedInfo} ref={(el) => { nameContainerRef.current = el; jobTitleContainerRef.current = el; }}>
                        <h3 ref={nameTextRef} className="semiboldMiddle">{truncatedName}</h3>
                        <div>
                            <span ref={jobTitleTextRef} className="regularMiddle">{truncatedJobTitle}</span>
                        </div>
                    </div>
                </header>
                <div className={classes.processBar}>
                    {/* First bar: Profile creation - always completed if we're showing this card */}
                    <div className={classes.processBarItem} style={{ background: '#0B0418' }}></div>
                    {/* Second bar: Projects - check if has published projects */}
                    <div className={classes.processBarItem} style={{ 
                    background: (Array.isArray(creative?.projects) 
                        ? creative.projects.some(p => p.status === 'PUBLISHED')
                        : creative?.projects?.items?.some(p => p.status === 'PUBLISHED')) ? '#0B0418' : '#DDE1E5' 
                    }}></div>
                    {/* Third bar: Packages - check if has active packages */}
                    <div className={classes.processBarItem} style={{ 
                    background: (Array.isArray(creative?.packages)
                        ? creative.packages.some(p => p.isActive === true)
                        : creative?.packages?.items?.some(p => p.isActive === true)) ? '#0B0418' : '#DDE1E5' 
                    }}></div>
                </div>
            </div>
            <div className={classes.processLinksContainer}>
                <div 
                    onClick={(e) => handleProcessLinkClick(e, `/creative-editing/${creative?.id}/profile`)} 
                    className={classes.processLink}
                    style={{ cursor: 'pointer' }}
                >
                    <div className={classes.processLinkContent}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="0.171429" y="0.171429" width="23.6571" height="23.6571" rx="11.8286" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.342857"/>
                            <line x1="7.17697" y1="12.1344" x2="10.0891" y2="15.1092" stroke="white" strokeWidth="0.888889"/>
                            <line x1="9.46713" y1="15.0998" x2="16.3328" y2="8.56951" stroke="white" strokeWidth="0.888889"/>
                        </svg>
                        <span className="regularSmall">Kreativ-Profil erstellen</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                        <path d="M0.3986 13.3203L6.38281 6.72166M6.36703 7.46886L0.382812 0.320312" stroke="#0B0418"/>
                    </svg>
                </div>
                <div 
                    onClick={(e) => handleProcessLinkClick(e, `/creative-editing/${creative?.id}/project`)} 
                    className={classes.processLink}
                    style={{ cursor: 'pointer' }}
                >
                    <div className={classes.processLinkContent}>
                        {/* Check for active projects: status === 'PUBLISHED' means active */}
                        {(Array.isArray(creative?.projects) 
                            ? creative.projects.some(p => p.status === 'PUBLISHED')
                            : creative?.projects?.items?.some(p => p.status === 'PUBLISHED')) ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="0.171429" y="0.171429" width="23.6571" height="23.6571" rx="11.8286" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.342857"/>
                            <line x1="7.17697" y1="12.1344" x2="10.0891" y2="15.1092" stroke="white" strokeWidth="0.888889"/>
                            <line x1="9.46713" y1="15.0998" x2="16.3328" y2="8.56951" stroke="white" strokeWidth="0.888889"/>
                        </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="white" stroke="#B1B6BB"/>
                            </svg>
                        )}
                        <span className="regularSmall">Arbeiten hinzufügen</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                        <path d="M0.3986 13.3203L6.38281 6.72166M6.36703 7.46886L0.382812 0.320312" stroke="#0B0418"/>
                    </svg>
                </div>
                <div 
                    onClick={(e) => handleProcessLinkClick(e, `/creative-editing/${creative?.id}/package`)} 
                    className={classes.processLink}
                    style={{ cursor: 'pointer' }}
                >
                    <div className={classes.processLinkContent}>
                        {/* Check for active packages: isActive === true means active */}
                        {(Array.isArray(creative?.packages)
                            ? creative.packages.some(p => p.isActive === true)
                            : creative?.packages?.items?.some(p => p.isActive === true)) ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect x="0.171429" y="0.171429" width="23.6571" height="23.6571" rx="11.8286" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.342857"/>
                                <line x1="7.17697" y1="12.1344" x2="10.0891" y2="15.1092" stroke="white" strokeWidth="0.888889"/>
                                <line x1="9.46713" y1="15.0998" x2="16.3328" y2="8.56951" stroke="white" strokeWidth="0.888889"/>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="white" stroke="#B1B6BB"/>
                            </svg>
                        )}
                        <span className="regularSmall">Pakete anbieten</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                        <path d="M0.3986 13.3203L6.38281 6.72166M6.36703 7.46886L0.382812 0.320312" stroke="#0B0418"/>
                    </svg>
                </div>
            </div>
        </Link>
      ) : (
        // Case: Creative is finished (PUBLISHED)
        <Link to={`/creative-editing/${creative?.id}`} className={classes.serviceCard}>
            <div className={classes.statusContainer}>
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill={creative?.status === "PUBLISHED" ? "#49BE12" : "#F7871E"}/>
                </svg>
                <span className="regularXxs">{creative?.status === "PUBLISHED" ? "aktiv" : "inaktiv"}</span>  
            </div>
            <div className={classes.contentContainer}>
                <header className={classes.publishedHeader}>
                    <div className={classes.publishedImageContainer}>
                        <ImageWithSkeleton
                          src={creative?.profilePicture}
                          alt={creative?.displayName || ""}
                          round
                          fallbackStorageKey={creative?.profilePictureKey}
                        />
                    </div>
                    <div className={classes.publishedInfo} ref={(el) => { nameContainerRef.current = el; jobTitleContainerRef.current = el; }}>
                        <h3 ref={nameTextRef} className="semiboldMiddle">{truncatedName}</h3>
                        <div>
                            <span ref={jobTitleTextRef} className="regularMiddle">{truncatedJobTitle}</span>
                        </div>
                    </div>
                </header>
                <div className={classes.tagsContainer} ref={tagsContainerRef}>
                    {creative?.skills.slice(0, visibleSkillsCount).map((service, index) => (
                        <div className={`${classes.serviceItem} ${index >= visibleSkillsCount ? classes.dropdownServiceItem : ''}`} key={index}>
                            <span className='regularMiddle'>{service}</span>
                        </div>
                    ))}
                    {creative?.skills.length > visibleSkillsCount && (
                        <button 
                            ref={triggerRef}
                            className={`${classes.allViewButton} mediumLarge`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleDropdown();
                            }}
                        >
                            +{creative?.skills.length - visibleSkillsCount}
                        </button>
                    )}
                    {showDropdown && (
                        <div ref={dropdownRef} className={classes.dropdownContainer}>
                            {creative?.skills.slice(visibleSkillsCount).map((service, index) => (
                                <div className={`${classes.serviceItem} ${classes.dropdownServiceItem}`} key={index + visibleSkillsCount}>
                                    <span className='regularMiddle'>{service}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className={classes.projectImagesContainerWrapper}>
                <div className={classes.projectImagesContainer}>
                    {isMusik && musicAudios.length > 0 ? (
                        <>
                            {musicAudios.map((audio, index) => {
                                const coverUrl = audio.processedCoverImage || '';
                                const key = audio?.audioKey || audio?.originalUrl || index;
                                return (
                                    <div
                                        className={`${classes.projectImageContainer} ${classes.audioTile}`}
                                        key={key}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleAudioPlayClick(e, audio);
                                        }}
                                    >
                                        {coverUrl ? (
                                            <ImageWithSkeleton
                                              src={coverUrl}
                                              alt={audio.title || "Audio"}
                                              loading="lazy"
                                              fallbackStorageKey={audio.coverImage || ''}
                                            />
                                        ) : (
                                            <div className={classes.audioPlaceholder} />
                                        )}
                                        <div className={classes.audioPlayOverlay}>
                                            <div className={classes.audioPlayButtonCircle}>
                                                <PlayIcon />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    ) : (
                        processedMediaItems.map((item, index) => (
                            <div className={classes.projectImageContainer} key={index}>
                                {/* {item.isLink && (
                                    <div className={classes.linkBadge}>
                                        <span>🔗</span>
                                    </div>
                                )} */}
                                {item.isLink ? (
                                    <ImageWithSkeleton
                                      src={item.thumbnailUrl}
                                      alt="Video-Vorschaubild"
                                      loading="lazy"
                                      fallbackStorageKey={typeof item.thumbnailUrl === 'string' ? item.thumbnailUrl : ''}
                                    />
                                ) : item.isVideoFile ? (
                                    <ImageWithSkeleton
                                      src={item.displayUrl}
                                      alt="Video-Vorschaubild"
                                      loading="lazy"
                                      fallbackStorageKey={
                                        typeof item.thumbnailUrl === 'string'
                                          ? item.thumbnailUrl
                                          : typeof item.url === 'string'
                                            ? item.url
                                            : ''
                                      }
                                    />
                                ) : item.displayUrl ? (
                                    <ImageWithSkeleton
                                      src={item.displayUrl}
                                      alt="Project Image"
                                      loading="lazy"
                                      fallbackStorageKey={typeof item.url === 'string' ? item.url : ''}
                                    />
                                ) : null}
                            </div>
                        ))
                    )}
                </div>
            </div>
            {isMusik && musicAudios.length > 0 && (
                <audio ref={audioRef} className={classes.hiddenAudio} />
            )}
        </Link>
      )}
    </>
  );
};

export default ServiceCard;
